import Header from "../../components/content_wrapper/header";
import { useGetBoxDetail } from "../../hooks/boxManagementApi/getBoxDetail";
import { useDeleteBox } from "../../hooks/boxManagementApi/deleteBox";
import { useGetBoxAuditLogs } from "../../hooks/boxManagementApi/getBoxAuditLogs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useUpdateCubicleStatus } from "../../hooks/boxManagementApi/cublicleStatus";
import { useEffect, useRef, useState, useMemo } from "react";
import CustomLaoder from "../../components/customLoader";
import { toast } from "react-toastify";
import Pagination from "../../utils/pagination";

const AUDIT_ACTION_BADGE: Record<string, { cls: string; icon: string; label: string }> = {
  created:    { cls: "c-pill--success",   icon: "fa-plus",       label: "Created" },
  assigned:   { cls: "c-pill--info",      icon: "fa-link",       label: "Assigned" },
  unassigned: { cls: "c-pill--warning",   icon: "fa-link-slash", label: "Unassigned" },
  deleted:    { cls: "c-pill--danger",    icon: "fa-trash",      label: "Deleted" },
};

const formatLogTimestamp = (ts: string | number | null): string => {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const summarizeAuditEntry = (log: any): string | null => {
  const c = log?.changes || {};
  const m = log?.metadata || {};
  // changes/metadata are Mixed-typed on the API side, so the response
  // interceptor's snake_case→camelCase transform still recurses into them —
  // read both forms, matching how the rest of this screen reads boxItemsDetail.
  const toHost = c.toKeyHostId ?? c.to_key_host_id;
  const fromHost = c.fromKeyHostId ?? c.from_key_host_id;
  const slotsDeleted = m.slotsDeleted ?? m.slots_deleted;
  const totalSlots = m.totalSlots ?? m.total_slots;
  if (toHost !== undefined) {
    return toHost ? `Assigned to host ${toHost}` : `Unassigned from host ${fromHost || "—"}`;
  }
  if (slotsDeleted !== undefined) return `${slotsDeleted} slot(s) removed with the box`;
  if (totalSlots !== undefined) return `${totalSlots} slot(s) created`;
  return null;
};

const SLOT_COLORS: Record<string, string> = {
  occupied: "#198754",
  reserved: "#ffc107",
  disabled: "#dc3545",
  available: "#e9ecef",
};

const formatStoredAt = (storedAt: number | null): string | null => {
  if (!storedAt) return null;
  const diff = Math.floor(Date.now() / 1000) - storedAt;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const getStateBadge = (state: string) => {
  switch (state) {
    case "occupied": return { cls: "c-pill--success", text: "Occupied",  icon: "fa-key" };
    case "reserved": return { cls: "c-pill--warning", text: "Reserved",  icon: "fa-clock" };
    case "disabled": return { cls: "c-pill--danger",  text: "Disabled",  icon: "fa-ban" };
    default:         return { cls: "c-pill--secondary",text: "Available", icon: "fa-square" };
  }
};

const Box_management_detail = () => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { id: urlId } = useParams<{ id: string }>();
  const [searchValue,    setSearchValue]    = useState("");
  const [statusFilter,   setStatusFilter]   = useState("all");
  const [stateFilter,    setStateFilter]    = useState("all");

  // Activity log — its own filter/sort/pagination, independent of the slot table above.
  const [auditActionFilter, setAuditActionFilter] = useState("all");
  const [auditFrom,         setAuditFrom]         = useState("");
  const [auditTo,           setAuditTo]           = useState("");
  const [auditSort,         setAuditSort]         = useState<"asc" | "desc">("desc");
  const [auditPage,         setAuditPage]         = useState(1);
  const [auditLimit,        setAuditLimit]        = useState(20);

  const data  = location.state;
  const boxId = data?.id || urlId;
  const scrollToAudit = Boolean((data as any)?.scrollToAudit);
  const auditSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollToAudit && auditSectionRef.current) {
      auditSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Only on arrival via the "View History" link — not on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToAudit]);

  useEffect(() => {
    if (!boxId) {
      toast.error("No box ID provided. Redirecting to box listing.");
      navigate("/boxmanagementlisting");
    }
  }, [boxId, navigate]);

  const { data: boxDetail, isLoading } = useGetBoxDetail({
    id: boxId,
    page: "1",
    limit: "500",
  });
  const boxItemsDetail = boxDetail?.data;

  const { mutateAsync: update } = useUpdateCubicleStatus();
  const { mutateAsync: deleteBoxMutation, isLoading: isDeleting } = useDeleteBox();
  const { data: auditLogData, isLoading: isLoadingAuditLogs } = useGetBoxAuditLogs({
    box_id: boxId,
    page: String(auditPage),
    limit: String(auditLimit),
    action: auditActionFilter !== "all" ? auditActionFilter : undefined,
    from: auditFrom ? new Date(auditFrom).toISOString() : undefined,
    // Date-only strings parse as UTC midnight — for "from" that's the right
    // inclusive lower bound, but the same parse on "to" would mean the very
    // START of the selected day, excluding almost everything the admin
    // picked it to include. Push it to the end of that UTC day instead.
    to: auditTo ? new Date(`${auditTo}T23:59:59.999Z`).toISOString() : undefined,
    sort: auditSort,
  });
  const auditLogs: any[] = auditLogData?.data?.logs || [];
  const auditTotalPages: number = auditLogData?.data?.totalPage || 1;

  const hasAuditFilters = auditActionFilter !== "all" || auditFrom || auditTo;
  const clearAuditFilters = () => {
    setAuditActionFilter("all");
    setAuditFrom("");
    setAuditTo("");
    setAuditSort("desc");
    setAuditPage(1);
  };
  const handleAuditPrevPage = () => setAuditPage((p) => Math.max(1, p - 1));
  const handleAuditNextPage = () => setAuditPage((p) => Math.min(auditTotalPages, p + 1));
  const handleAuditRowLimitChange = (n: number) => { setAuditLimit(n); setAuditPage(1); };

  const allCubicles: any[] = useMemo(
    () => boxItemsDetail?.cubicle_details || boxItemsDetail?.cubicleDetails || [],
    [boxItemsDetail]
  );

  const statistics = useMemo(() => {
    const occupied  = allCubicles.filter((c) => c.state === "occupied").length;
    const reserved  = allCubicles.filter((c) => c.state === "reserved").length;
    const available = allCubicles.filter((c) => c.state === "available").length;
    const disabled  = allCubicles.filter((c) => c.state === "disabled").length;
    return { total: allCubicles.length, occupied, reserved, available, disabled };
  }, [allCubicles]);

  const filteredCubicles = useMemo(() => {
    let list = [...allCubicles];
    if (searchValue) {
      const q = searchValue.toLowerCase();
      list = list.filter((c) =>
        String(c.cubeNumber ?? c.cube_number).includes(q) ||
        ((c.addedKey || c.added_key) || "").toLowerCase().includes(q) ||
        ((c.keyringId || c.keyring_id) || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter === "active")    list = list.filter((c) => c.status === true);
    if (statusFilter === "suspended") list = list.filter((c) => c.status === false);
    if (stateFilter !== "all")        list = list.filter((c) => c.state === stateFilter);
    return list;
  }, [allCubicles, searchValue, statusFilter, stateFilter]);

  const handleToggle = async (cube_id: any, currentStatus: boolean) => {
    try {
      const res: any = await update({ _id: boxId, cube_id, status: !currentStatus });
      if (res.success) {
        toast.success(`Cubicle ${!currentStatus ? "activated" : "suspended"} successfully`);
      } else {
        toast.error("Failed to update cubicle status");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error updating cubicle status");
    }
  };

  const assignedHost = boxItemsDetail?.assignedKeyHost || boxItemsDetail?.assigned_key_host || null;
  const canDelete = !assignedHost && statistics.occupied === 0 && statistics.reserved === 0;
  const deleteBlockedReason = assignedHost
    ? "Unassign this box from its key host before deleting it"
    : statistics.occupied > 0 || statistics.reserved > 0
    ? "Can't delete a box with key slots assigned"
    : null;

  const handleDeleteBox = async () => {
    if (!canDelete) return;
    const label = boxItemsDetail?.box_id || boxItemsDetail?.boxId || boxId;
    if (!window.confirm(`Permanently delete box "${label}"? This cannot be undone.`)) return;
    try {
      const res: any = await deleteBoxMutation({ id: boxId });
      if (res?.success) {
        toast.success(res?.message || "Box deleted successfully");
        navigate("/boxmanagementlisting");
      } else {
        toast.error(res?.message || "Failed to delete box");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error deleting box");
    }
  };

  const hasFilters = searchValue || statusFilter !== "all" || stateFilter !== "all";
  const clearFilters = () => { setSearchValue(""); setStatusFilter("all"); setStateFilter("all"); };

  const statCards = [
    { label: "Total Slots", value: statistics.total,    color: "primary", icon: "fa-grid-2",      filter: null },
    { label: "Available",   value: statistics.available,color: "success", icon: "fa-square",      filter: "available" },
    { label: "Occupied",    value: statistics.occupied, color: "info",    icon: "fa-key",         filter: "occupied" },
    { label: "Reserved",    value: statistics.reserved, color: "warning", icon: "fa-clock",       filter: "reserved" },
    { label: "Disabled",    value: statistics.disabled, color: "danger",  icon: "fa-ban",         filter: "disabled" },
  ];

  return (
    <div className="main-wrapper">
      <Header title="Box Details" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section profile-section">
            <div className="container">

              {/* ── Overview card ── */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  {/* Top row: back + identity */}
                  <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
                    <div className="d-flex align-items-center gap-3">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => navigate(-1)}
                      >
                        <i className="fa-solid fa-chevron-left me-1" />Back
                      </button>
                      <div>
                        <h5 className="mb-0 fw-bold">
                          <i className="fa-solid fa-box me-2 text-primary" />
                          Box #{boxItemsDetail?.box_id || boxItemsDetail?.boxId || "—"}
                        </h5>
                        <div className="d-flex flex-wrap gap-3 mt-1">
                          {boxItemsDetail?.location && (
                            <span className="text-muted small">
                              <i className="fa-solid fa-location-dot me-1" />
                              {boxItemsDetail.location}
                            </span>
                          )}
                          {(boxItemsDetail?.assignedKeyHost || boxItemsDetail?.assigned_key_host) && (
                            <span className="text-muted small">
                              <i className="fa-solid fa-store me-1" />
                              {boxItemsDetail.assignedKeyHost || boxItemsDetail.assigned_key_host}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleDeleteBox}
                      disabled={!canDelete || isDeleting}
                      title={deleteBlockedReason || "Permanently delete this box"}
                    >
                      <i className="fa-solid fa-trash me-1" />
                      {isDeleting ? "Deleting…" : "Delete Box"}
                    </button>
                  </div>
                  {deleteBlockedReason && (
                    <div className="small text-muted mb-2">
                      <i className="fa-solid fa-circle-info me-1" />
                      {deleteBlockedReason}
                    </div>
                  )}

                  {/* Stats row — clickable to filter */}
                  <div className="row g-3">
                    {statCards.map((s) => (
                      <div className="col" key={s.label}>
                        <div
                          className={`card border-0 h-100 bg-${s.color} bg-opacity-10`}
                          style={{ cursor: s.filter ? "pointer" : "default", border: `1px solid var(--bs-${s.color}-rgb, #ccc)` }}
                          onClick={() =>
                            s.filter && setStateFilter(stateFilter === s.filter ? "all" : s.filter)
                          }
                        >
                          <div className="card-body p-3 text-center">
                            <i className={`fa-solid ${s.icon} text-${s.color} mb-1 d-block`} />
                            <div className={`h3 mb-0 fw-bold text-${s.color}`}>{s.value}</div>
                            <div className="small text-muted">{s.label}</div>
                            {s.filter && stateFilter === s.filter && (
                              <div className="small text-primary mt-1">
                                <i className="fa-solid fa-filter me-1" />Filtered
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Slot map ── */}
              {allCubicles.length > 0 && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white d-flex align-items-center justify-content-between py-3">
                    <h6 className="mb-0">
                      <i className="fa-solid fa-map-pin me-2 text-muted" />
                      Slot Map
                    </h6>
                    <div className="d-flex flex-wrap gap-3 small text-muted">
                      {[
                        { color: SLOT_COLORS.occupied,  label: "Occupied" },
                        { color: SLOT_COLORS.reserved,  label: "Reserved" },
                        { color: SLOT_COLORS.available, label: "Available", border: true },
                        { color: SLOT_COLORS.disabled,  label: "Disabled" },
                      ].map((l) => (
                        <span key={l.label} className="d-flex align-items-center gap-1">
                          <span
                            style={{
                              width: 14, height: 14, borderRadius: 3,
                              background: l.color,
                              border: l.border ? "1px solid #ced4da" : "none",
                              display: "inline-block",
                            }}
                          />
                          {l.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="d-flex flex-wrap gap-2">
                      {allCubicles.map((slot: any) => {
                        const slotNum  = slot.cubeNumber  ?? slot.cube_number;
                        const slotId   = slot.cubeId      ?? slot.cube_id;
                        const keyLabel = slot.addedKey    ?? slot.added_key;
                        return (
                          <div
                            key={slotId}
                            title={`Slot #${slotNum} — ${slot.state}${keyLabel ? ": " + keyLabel : ""}`}
                            style={{
                              width: 44, height: 44,
                              background: SLOT_COLORS[slot.state] || SLOT_COLORS.available,
                              borderRadius: 6,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 12, fontWeight: 700,
                              color: slot.state === "available" ? "#6c757d" : slot.state === "reserved" ? "#333" : "#fff",
                              border: stateFilter === slot.state ? "2px solid #0d6efd" : "2px solid transparent",
                              boxShadow: "0 1px 3px rgba(0,0,0,.1)",
                            }}
                          >
                            {slotNum}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Cubicle table ── */}
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white py-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">
                      <i className="fa-solid fa-list me-2 text-muted" />
                      Cubicle Details
                    </h6>
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-muted">
                        {filteredCubicles.length} of {allCubicles.length} slots
                      </small>
                      {hasFilters && (
                        <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                          <i className="fa-solid fa-filter-circle-xmark me-1" />Clear
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="row g-2">
                    <div className="col-md-5">
                      <div className="form-block p-relative">
                        <input
                          type="search"
                          className="form-control form-control-sm"
                          placeholder="Search by slot #, key name or keyring code…"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          autoComplete="off"
                        />
                        <span className="search-icon" />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select form-select-sm"
                        value={stateFilter}
                        onChange={(e) => setStateFilter(e.target.value)}
                      >
                        <option value="all">All States</option>
                        <option value="occupied">Occupied</option>
                        <option value="available">Available</option>
                        <option value="reserved">Reserved</option>
                        <option value="disabled">Disabled</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All Cubicles</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4" style={{ width: 80 }}>Slot</th>
                          <th>Key / Keyring</th>
                          <th>State</th>
                          <th>Stored</th>
                          <th className="text-center" style={{ width: 90 }}>Enabled</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCubicles.length > 0 ? (
                          filteredCubicles.map((slot: any, index: number) => {
                            const slotNum    = slot.cubeNumber  ?? slot.cube_number;
                            const slotId     = slot.cubeId      ?? slot.cube_id;
                            const addedKey   = slot.addedKey    ?? slot.added_key;
                            const keyringId  = slot.keyringId   ?? slot.keyring_id;
                            const storedAt   = slot.storedAt    ?? slot.stored_at;
                            const badge      = getStateBadge(slot.state);
                            const isOccupied = slot.state === "occupied";
                            const isReserved = slot.state === "reserved";
                            const storedSince = formatStoredAt(storedAt);

                            return (
                              <tr
                                key={index}
                                className={
                                  isOccupied ? "table-success" :
                                  isReserved ? "table-warning" : ""
                                }
                                style={{ opacity: slot.state === "disabled" ? 0.6 : 1 }}
                              >
                                <td className="ps-4">
                                  <strong className="text-primary fs-6">#{slotNum}</strong>
                                </td>
                                <td>
                                  {isOccupied || isReserved ? (
                                    <div>
                                      {addedKey && (
                                        <div className="fw-semibold">
                                          <i className="fa-solid fa-key me-1 text-success" />
                                          {addedKey}
                                        </div>
                                      )}
                                      {keyringId && (
                                        <div className="small text-muted">
                                          <i className="fa-solid fa-qrcode me-1" />
                                          {keyringId}
                                        </div>
                                      )}
                                      {!addedKey && !keyringId && (
                                        <span className="text-muted fst-italic small">Key info unavailable</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td>
                                  <span className={`c-pill ${badge.cls}`}>
                                    <i className={`fa-solid ${badge.icon} me-1`} />
                                    {badge.text}
                                  </span>
                                </td>
                                <td>
                                  {storedSince ? (
                                    <span className="small text-muted">
                                      <i className="fa-regular fa-clock me-1" />
                                      {storedSince}
                                    </span>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td className="text-center">
                                  <span className="table-check">
                                    <input
                                      type="checkbox"
                                      id={`toggle${index}`}
                                      onChange={() => handleToggle(slotId, slot.status)}
                                      checked={slot.status === true}
                                      disabled={isOccupied}
                                    />
                                    <label
                                      htmlFor={`toggle${index}`}
                                      title={
                                        isOccupied
                                          ? "Cannot disable while occupied"
                                          : slot.status
                                          ? "Click to suspend"
                                          : "Click to activate"
                                      }
                                    />
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={5} className="text-center py-5">
                              <i className="fa-solid fa-inbox fa-3x text-muted mb-3 d-block" />
                              <p className="text-muted mb-1">
                                {hasFilters ? "No slots match your filters" : "No slots found"}
                              </p>
                              {hasFilters && (
                                <button className="btn btn-sm btn-link" onClick={clearFilters}>
                                  Clear filters
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* ── Activity log ── */}
              <div
                ref={auditSectionRef}
                className="card border-0 shadow-sm mt-4"
                style={scrollToAudit ? { boxShadow: "0 0 0 3px #0d6efd" } : undefined}
              >
                <div className="card-header bg-white py-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">
                      <i className="fa-solid fa-clock-rotate-left me-2 text-muted" />
                      Activity Log
                    </h6>
                    <div className="d-flex align-items-center gap-2">
                      {isLoadingAuditLogs && <span className="spinner-border spinner-border-sm text-muted" />}
                      {hasAuditFilters && (
                        <button className="btn btn-sm btn-outline-secondary" onClick={clearAuditFilters}>
                          <i className="fa-solid fa-filter-circle-xmark me-1" />Clear
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="row g-2">
                    <div className="col-md-3">
                      <select
                        className="form-select form-select-sm"
                        value={auditActionFilter}
                        onChange={(e) => { setAuditActionFilter(e.target.value); setAuditPage(1); }}
                      >
                        <option value="all">All Actions</option>
                        <option value="created">Created</option>
                        <option value="assigned">Assigned</option>
                        <option value="unassigned">Unassigned</option>
                        <option value="deleted">Deleted</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={auditFrom}
                        onChange={(e) => { setAuditFrom(e.target.value); setAuditPage(1); }}
                        title="From date"
                      />
                    </div>
                    <div className="col-md-3">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={auditTo}
                        onChange={(e) => { setAuditTo(e.target.value); setAuditPage(1); }}
                        title="To date"
                      />
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select form-select-sm"
                        value={auditSort}
                        onChange={(e) => setAuditSort(e.target.value as "asc" | "desc")}
                      >
                        <option value="desc">Newest first</option>
                        <option value="asc">Oldest first</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4" style={{ width: 120 }}>Action</th>
                          <th>Details</th>
                          <th>By</th>
                          <th style={{ width: 180 }}>When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLogs.length > 0 ? (
                          auditLogs.map((log: any, index: number) => {
                            const badge = AUDIT_ACTION_BADGE[log.action] || {
                              cls: "c-pill--secondary", icon: "fa-circle-info", label: log.action,
                            };
                            const summary = summarizeAuditEntry(log);
                            return (
                              <tr key={log._id || index}>
                                <td className="ps-4">
                                  <span className={`c-pill ${badge.cls}`}>
                                    <i className={`fa-solid ${badge.icon} me-1`} />
                                    {badge.label}
                                  </span>
                                </td>
                                <td>
                                  {summary ? (
                                    <span className="small">{summary}</span>
                                  ) : (
                                    <span className="text-muted small">—</span>
                                  )}
                                </td>
                                <td>
                                  <span className="small">
                                    {log.performedByName || log.performed_by_name || log.performedByEmail || log.performed_by_email || "System"}
                                  </span>
                                </td>
                                <td>
                                  <span className="small text-muted">
                                    {formatLogTimestamp(log.timestamp)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={4} className="text-center py-5">
                              <i className="fa-solid fa-inbox fa-3x text-muted mb-3 d-block" />
                              <p className="text-muted mb-1">
                                {hasAuditFilters ? "No activity matches your filters" : "No activity recorded for this box yet"}
                              </p>
                              {hasAuditFilters && (
                                <button className="btn btn-sm btn-link" onClick={clearAuditFilters}>
                                  Clear filters
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {auditLogs.length > 0 && (
                    <Pagination
                      totalPages={auditTotalPages}
                      currentPage={auditPage}
                      handlePrevPage={handleAuditPrevPage}
                      handleNextPage={handleAuditNextPage}
                      handleRowLimitChange={handleAuditRowLimitChange}
                    />
                  )}
                </div>
              </div>

            </div>
          </section>
        </div>
      </CustomLaoder>
    </div>
  );
};

export default Box_management_detail;
